import { Separator } from "@/components/ui";
import { getDisplay } from "@curai/js-fhir-tools";
import type * as fhirTypes from "fhir/r4";
import { AVCallSummaryAddendum } from "./AVCallSummaryAddendum";
import { type Addendum, parseAddendumStatus } from "./types";

interface AVCallSummaryAddendumsProps {
  documentReferences: fhirTypes.DocumentReference[];
  onAddAddendum: (addendum: Addendum) => void;
  addedAddendums: Addendum[];
  canWriteSubjective: boolean;
}

const SUBJECTIVE_ADDENDUM_METADATA_URL =
  "https://fhir-ig.curaihealth.com/StructureDefinition/subjective-addendum-metadata";
const AV_CALL_SUMMARY_TYPE_CODE = "av-call-summary";

export const AVCallSummaryAddendums = ({
  documentReferences,
  onAddAddendum,
  addedAddendums,
  canWriteSubjective,
}: AVCallSummaryAddendumsProps) => {
  const avCallExtensions = documentReferences.flatMap((docRef) =>
    (docRef.extension || []).filter((ext) => {
      if (ext.url !== SUBJECTIVE_ADDENDUM_METADATA_URL) return false;
      const typeExt = (ext.extension || []).find((e) => e.url === "type");
      const typeCode = typeExt?.valueCodeableConcept?.coding?.[0]?.code;
      return typeCode === AV_CALL_SUMMARY_TYPE_CODE;
    }),
  );

  if (!avCallExtensions.length) {
    return null;
  }

  return (
    <div className="mt-4">
      <Separator />
      {avCallExtensions.map((extension) => {
        const addendum = extension.extension || [];
        const noteExt = addendum.find((ext) => ext.url === "note");
        const note = noteExt?.valueAnnotation;
        const summary = note?.text;
        const time = note?.time;
        const statusExt = addendum.find((ext) => ext.url === "status");
        const status = parseAddendumStatus(
          statusExt?.valueCodeableConcept
            ? getDisplay(statusExt.valueCodeableConcept)
            : undefined,
        );
        // AV call summary addendums identify themselves via the parent
        // extension's id (set by the FHIR server). There is no identifier
        // sub-extension for this addendum type.
        const addendumId = extension.id;

        if (!addendumId || !summary || !time) {
          return null;
        }

        return (
          <AVCallSummaryAddendum
            key={addendumId}
            summary={summary}
            time={time}
            status={status}
            addendumId={addendumId}
            onAddAddendum={onAddAddendum}
            addedAddendums={addedAddendums}
            canWriteSubjective={canWriteSubjective}
          />
        );
      })}
    </div>
  );
};
